import { useEffect, useState } from "react";
import { Menu, X, Phone, Star, Instagram } from "lucide-react";
import { Button } from "./ui/button";
import { BRAND, LOGO_URL, GMB_URL } from "../mock";
import { openExternal } from "../lib/openExternal";
import { buildBookingSmsHref } from "../lib/bookingSms";

const LINKS = [
  { label: "Home", href: "/#home" },
  { label: "$550 Offer", href: "/offer", highlight: true },
  { label: "Ceramic", href: "/ceramic-coating-mississauga" },
  { label: "Reviews", href: "/reviews" },
  { label: "Blog", href: "/blog" },
  { label: "Media", href: "/media" },
  { label: "Contact", href: "/#contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-[background,backdrop-filter,border] duration-500 ${
        scrolled
          ? "bg-[#120127]/85 backdrop-blur-xl border-b border-white/10"
          : "bg-transparent"
      }`}
    >
      <div className="hidden md:flex items-center justify-between bg-jester-purple text-white text-xs uppercase tracking-[0.2em] py-1.5 px-10">
        <div className="flex items-center gap-4">
          <span className="font-label">Mississauga</span>
          <span className="opacity-60">|</span>
          <span className="font-label">Vaughan</span>
          <span className="opacity-60">|</span>
          <span className="font-label">Open Daily · 2 PM to 12 AM</span>
        </div>
        <div className="flex items-center gap-5">
          <a
            href={BRAND.socials.googleWriteReview}
            target="_blank"
            rel="noopener noreferrer"
            className="font-label hover:text-acid transition-colors"
           onClick={(e) => { e.preventDefault(); openExternal(BRAND.socials.googleWriteReview); }}>
            ✦ Leave a Google Review
          </a>
          <span className="opacity-40">|</span>
          <a
            href={GMB_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 hover:text-acid transition-colors"
           onClick={(e) => { e.preventDefault(); openExternal(GMB_URL); }}>
            {[...Array(5)].map((_, i) => (
              <Star
                key={i}
                className="w-3 h-3"
                style={{ color: "#FDC814", fill: "#FDC814" }}
              />
            ))}
            <span className="font-label">5.0 · {BRAND.rating.count}+ Google reviews</span>
          </a>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-10 flex items-center justify-between h-20">
        <a href="/" className="flex items-center gap-3 group">
          <img
            src={LOGO_URL}
            alt="Jester Werks logo"
            className="w-12 h-12 object-contain group-hover:scale-110 transition-transform duration-500 drop-shadow-[0_0_20px_rgba(141,57,238,0.5)]"
          />
          <div className="leading-none">
            <div className="font-display text-xl md:text-2xl tracking-wider">
              JESTER WERKS
            </div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-acid font-label mt-1">
              Ceramic Coating Studio
            </div>
          </div>
        </a>

        <nav className="hidden lg:flex items-center gap-7">
          {LINKS.map((l) => (
            <a
              key={l.label}
              href={l.href}
              className={
                l.highlight
                  ? "text-sm font-label uppercase tracking-[0.2em] px-3 py-1 rounded-full border border-acid/50 bg-acid/10 text-acid hover:bg-acid hover:text-[#120127] transition-colors duration-300"
                  : "text-sm font-label uppercase tracking-[0.2em] text-white/70 hover:text-acid transition-colors duration-300"
              }
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={BRAND.socials.instagram}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Instagram"
            className="hidden md:flex w-10 h-10 rounded-full border border-white/15 items-center justify-center hover:bg-jester-purple hover:border-jester text-white transition-colors"
          >
            <Instagram className="w-4 h-4" />
          </a>
          <a
            href={`tel:${BRAND.phone}`}
            className="hidden md:flex items-center gap-2 text-sm text-white/90 hover:text-acid transition"
          >
            <Phone className="w-4 h-4" /> {BRAND.phone}
          </a>
          <Button
            asChild
            className="hidden md:inline-flex bg-acid hover:bg-[#bff020] text-[#120127] font-label tracking-[0.2em] text-sm rounded-full px-6 h-10 shadow-[0_0_25px_rgba(168,224,0,0.4)]"
          >
            <a href="/offer" data-testid="book-now-nav">
              Book Now
            </a>
          </Button>
          <button
            onClick={() => setOpen(!open)}
            className="lg:hidden p-2 text-white"
            aria-label="menu"
          >
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      <div
        className={`lg:hidden overflow-hidden transition-[max-height] duration-500 ${
          open ? "max-h-[500px]" : "max-h-0"
        } bg-[#0c0119]/95 backdrop-blur-xl border-b border-white/10`}
      >
        <div className="flex flex-col px-6 py-6 gap-5">
          {LINKS.map((l) => (
            <a
              key={l.label}
              href={l.href}
              onClick={() => setOpen(false)}
              className={
                l.highlight
                  ? "text-sm font-label uppercase tracking-[0.2em] px-3 py-2 rounded-full border border-acid/50 bg-acid/10 text-acid text-center"
                  : "text-sm font-label uppercase tracking-[0.2em] text-white/80 hover:text-acid"
              }
            >
              {l.label}
            </a>
          ))}
          <a
            href={BRAND.socials.instagram}
            target="_blank"
            rel="noopener noreferrer"
            className="text-acid font-label tracking-[0.2em] flex items-center gap-2"
          >
            <Instagram className="w-4 h-4" /> Instagram
          </a>
          <a href={`tel:${BRAND.phone}`} className="text-acid font-semibold">
            {BRAND.phone}
          </a>
          <a
            href="/offer"
            onClick={() => setOpen(false)}
            className="mt-2 inline-flex items-center justify-center gap-2 px-6 h-12 rounded-full bg-acid text-[#120127] font-label tracking-[0.2em] text-sm uppercase shadow-[0_0_25px_rgba(168,224,0,0.4)]"
            data-testid="book-now-mobile"
          >
            Book Now. $550 Offer
          </a>
        </div>
      </div>
    </header>
  );
}
